import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Phone, ArrowRight, Anchor, Crown, Compass, Leaf, Sailboat, Calendar, Mail, Home, MapPin } from 'lucide-react';
import { Button } from '../ui/button';

const navLinks = [
  { name: 'Accueil', path: '/', desc: 'Retour à la page principale', icon: Home },
  { name: 'Destinations', path: '/destinations-croisieres-catamaran', desc: 'Nos 7 croisières catamaran', icon: Compass },
  { name: 'Slow Tourisme', path: '/slow-tourisme-croisieres-responsables', desc: 'Notre engagement responsable', icon: Leaf },
  { name: 'Navires', path: '/les-navires-disponibles', desc: 'Notre flotte de catamarans', icon: Sailboat },
  { name: 'Club', path: '/carte-club-des-voyageurs', desc: 'Avantages fidélité', icon: Crown },
  { name: 'Réservation', path: '/reservation-cabines-privatisation-catamaran-sognudimare', desc: 'Réserver votre croisière', icon: Calendar },
  { name: 'Contact', path: '/contact', desc: 'Nous écrire', icon: Mail },
];

// Destinations shown on the right side of the desktop menu (expanded panel)
const destinationsList = [
  {
    slug: 'tour-de-corse-croisiere-catamaran',
    name: "L'Inoubliable Tour de Corse",
    region: 'Tour de Corse',
    duration: '14 jours',
    image: 'https://static.wixstatic.com/media/ce6ce7_0ce032b8fe2e4bf58652c8d18e897478~mv2.jpg/v1/fill/w_300,h_200,q_85/SOGNUDIMARE%20CROISIERE%20CATAMARAN%20DEPUIS%20AJACCIO%20copie.jpg',
  },
  {
    slug: 'croisiere-catamaran-corse-du-sud',
    name: 'La Radieuse Corse du Sud',
    region: 'Corse du Sud',
    duration: '8 jours',
    image: 'https://customer-assets.emergentagent.com/job_ec38d06e-f44e-452a-a6c5-760d9ddaeb76/artifacts/4uqnxcxl_Bonifacio.jpeg',
  },
  {
    slug: 'croisiere-catamaran-ouest-corse',
    name: "L'Indomptée Ouest Corse",
    region: 'Ouest Corse',
    duration: '8 jours',
    image: 'https://customer-assets.emergentagent.com/job_ec38d06e-f44e-452a-a6c5-760d9ddaeb76/artifacts/0hycid8d_IMG-20220714-WA0039.jpg',
  },
  {
    slug: 'croisiere-catamaran-sardaigne-corse',
    name: 'La Sublime Sardaigne & Corse',
    region: 'Sardaigne',
    duration: '8 jours',
    image: 'https://customer-assets.emergentagent.com/job_ec38d06e-f44e-452a-a6c5-760d9ddaeb76/artifacts/azb8sv93_CALA%20DI%20COTICCIO%202.jpg',
  },
  {
    slug: 'croisiere-catamaran-grece-authentique',
    name: 'La Grèce Authentique',
    region: 'Grèce — Ionienne',
    duration: '8 jours',
    image: 'https://static.wixstatic.com/media/ce6ce7_227bd245ee9c48eca3a0f527080bf3a7~mv2.jpg/v1/fill/w_300,h_200,q_85/cephalonie%20grece.jpg',
  },
  {
    slug: 'croisiere-catamaran-grece-cyclades-2-semaines',
    name: 'La Grèce & Les Cyclades',
    region: 'Cyclades',
    duration: '14 jours',
    image: 'https://customer-assets.emergentagent.com/job_ec38d06e-f44e-452a-a6c5-760d9ddaeb76/artifacts/5k1x1u8k_17cd96cd47b005368a41bff99cb2f214-25881-athens-sounion-cape-sounion-sunset-tour-03.jpg',
  },
  {
    slug: 'croisiere-catamaran-grenadines-caraibes',
    name: 'Les Éclatantes Grenadines',
    region: 'Caraïbes',
    duration: '8 jours',
    image: 'https://customer-assets.emergentagent.com/job_ec38d06e-f44e-452a-a6c5-760d9ddaeb76/artifacts/cpem283a_Tobagocays2018.jpg',
  },
];

// Day charter (sorties à la journée) — affiché en bas du menu, séparé des croisières longues
const dayChartersList = [
  {
    href: '/les-navires-disponibles#day-charter',
    name: 'Day Charter · Ajaccio',
    region: 'Au départ d\'Ajaccio',
    duration: '1 journée',
    image: 'https://customer-assets.emergentagent.com/job_ec38d06e-f44e-452a-a6c5-760d9ddaeb76/artifacts/i5dyiztx_LES%20SANGUINAIRES.jpg',
  },
  {
    href: '/les-navires-disponibles#day-charter',
    name: 'Day Charter · Porto-Vecchio',
    region: 'Au départ de Porto-Vecchio',
    duration: '1 journée',
    image: 'https://customer-assets.emergentagent.com/job_ec38d06e-f44e-452a-a6c5-760d9ddaeb76/artifacts/jm0mvrgj_lavezzi.jpg',
  },
];

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [animating, setAnimating] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const closeMenu = useCallback(() => {
    setAnimating(false);
    setTimeout(() => setIsOpen(false), 350);
  }, []);

  useEffect(() => { closeMenu(); }, [location.pathname, closeMenu]);

  useEffect(() => {
    if (isOpen) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = '';
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  const openMenu = useCallback(() => {
    setIsOpen(true);
    setAnimating(true);
  }, []);

  const isActive = (path) => location.pathname === path;

  return (
    <>
      {/* ─── Top Nav Bar — minimal "luxe" ─── */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? 'bg-white/95 backdrop-blur-md shadow-sm py-2' : 'bg-transparent py-2.5'
        }`}
        data-testid="navbar"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between gap-3">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="logo-link">
              <img
                src="https://static.wixstatic.com/media/ce6ce7_a82e3e86741143d6ab7acd99c121af7b~mv2.png/v1/fill/w_600,h_300,al_c,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/croisieres%20catamaran%20corse%20sognudimare.png"
                alt="SOGNUDIMARE"
                className={`object-contain transition-all duration-300 ${scrolled ? 'h-9' : 'h-10 sm:h-12'}`}
                style={{ filter: scrolled ? 'none' : 'drop-shadow(0 0 6px rgba(0,0,0,0.45))' }}
              />
              <span
                className={`hidden sm:inline transition-all duration-300 ${scrolled ? 'text-sm' : 'text-base'}`}
                style={{
                  fontFamily: "'Comfortaa', cursive",
                  fontWeight: 700,
                  letterSpacing: '0.02em',
                  textShadow: scrolled ? 'none' : '0 1px 4px rgba(0,0,0,0.5)',
                }}
              >
                <span className={scrolled ? 'text-[#1D3557]' : 'text-white'}>sognu</span>
                <span className="text-[#2A9D8F]">di</span>
                <span className={scrolled ? 'text-[#1D3557]' : 'text-white'}>mare</span>
              </span>
            </Link>

            {/* Right cluster: phone (desktop) + Réserver + Burger */}
            <div className="flex items-center gap-2 sm:gap-3">
              <a
                href="tel:0495729028"
                className={`hidden md:inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-full transition-colors ${
                  scrolled ? 'text-[#1D3557] hover:bg-[#1D3557]/5' : 'text-white hover:bg-white/10'
                }`}
                data-testid="phone-link"
              >
                <Phone className="h-3.5 w-3.5" />
                <span className="tracking-wide">04 95 72 90 28</span>
              </a>

              <Link
                to="/reservation-cabines-privatisation-catamaran-sognudimare"
                className="group inline-flex items-center gap-1.5 bg-[#C4724F] hover:bg-[#A0552F] text-white font-semibold text-xs sm:text-sm pl-3 pr-2.5 py-1.5 rounded-full shadow-sm transition-all hover:-translate-y-0.5"
                data-testid="cta-reserve-btn"
              >
                <Anchor className="h-3.5 w-3.5" />
                <span>Réserver</span>
                <ArrowRight className="h-3 w-3 group-hover:translate-x-0.5 transition-transform" />
              </Link>

              {/* Burger — always visible (desktop + mobile) */}
              <button
                onClick={isOpen ? closeMenu : openMenu}
                className={`p-2 rounded-full relative z-[60] transition-all ${
                  scrolled ? 'text-[#1D3557] hover:bg-[#1D3557]/5' : 'text-white hover:bg-white/10'
                }`}
                data-testid="menu-btn"
                aria-label="Ouvrir le menu"
              >
                <div className="relative w-5 h-5 flex items-center justify-center">
                  <span className={`absolute block h-0.5 w-5 bg-current rounded transition-all duration-300 ${isOpen ? 'rotate-45' : '-translate-y-1.5'}`} />
                  <span className={`absolute block h-0.5 w-5 bg-current rounded transition-all duration-300 ${isOpen ? 'opacity-0 scale-0' : 'opacity-100'}`} />
                  <span className={`absolute block h-0.5 w-5 bg-current rounded transition-all duration-300 ${isOpen ? '-rotate-45' : 'translate-y-1.5'}`} />
                </div>
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* ─── Fullscreen Sliding Menu (desktop + mobile) ─── */}
      {isOpen && (
        <div className="fixed inset-0 z-[55]" data-testid="fullscreen-menu">
          {/* Backdrop */}
          <div
            className={`absolute inset-0 bg-[#0A2540] transition-opacity duration-500 ${animating ? 'opacity-100' : 'opacity-0'}`}
            onClick={closeMenu}
          />

          {/* Menu Content */}
          <div className={`relative h-full flex flex-col transition-all duration-500 ${animating ? 'opacity-100' : 'opacity-0'}`}>
            {/* Top: Logo */}
            <div
              className={`pt-16 pb-3 px-6 sm:px-10 transition-all duration-500 delay-100 ${
                animating ? 'translate-y-0 opacity-100' : '-translate-y-4 opacity-0'
              }`}
            >
              <div className="max-w-7xl mx-auto">
                <div className="flex items-center gap-3">
                  <img
                    src="https://static.wixstatic.com/media/ce6ce7_a82e3e86741143d6ab7acd99c121af7b~mv2.png/v1/fill/w_600,h_300,al_c,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/croisieres%20catamaran%20corse%20sognudimare.png"
                    alt="SOGNUDIMARE"
                    className="h-9 object-contain"
                    style={{ filter: 'brightness(1.1)' }}
                  />
                  <span style={{ fontFamily: "'Comfortaa', cursive", fontWeight: 700, letterSpacing: '0.05em' }}>
                    <span className="text-white text-base">sognu</span>
                    <span className="text-[#2A9D8F] text-base">di</span>
                    <span className="text-white text-base">mare</span>
                  </span>
                </div>
                <div className="h-px bg-gradient-to-r from-[#D4AF37]/40 via-[#D4AF37]/20 to-transparent mt-3" />
              </div>
            </div>

            {/* Middle: Nav Links (left) + Destinations grid (right, desktop only) */}
            <div className="flex-1 px-6 sm:px-10 py-1 overflow-y-auto">
              <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-[1fr_1.4fr] gap-x-12 gap-y-6">

                {/* LEFT — Primary nav list */}
                <div>
                  <div className="hidden lg:flex items-center gap-2 mb-3">
                    <span className="text-[#D4AF37]/60 font-mono text-[10px] uppercase tracking-[0.3em]">Menu</span>
                    <div className="flex-1 h-px bg-gradient-to-r from-[#D4AF37]/30 to-transparent" />
                  </div>
                  {navLinks.map((link, i) => {
                    const Icon = link.icon;
                    const active = isActive(link.path);
                    return (
                      <Link
                        key={link.path}
                        to={link.path}
                        onClick={closeMenu}
                        className={`group flex items-center gap-3 py-2.5 sm:py-3 border-b border-white/5 transition-all duration-500 ${
                          animating ? 'translate-x-0 opacity-100' : 'translate-x-8 opacity-0'
                        }`}
                        style={{ transitionDelay: animating ? `${150 + i * 50}ms` : '0ms' }}
                        data-testid={`menu-link-${link.name.toLowerCase().replace(/[ é]/g, '-')}`}
                      >
                        <span className="text-[#D4AF37]/40 font-mono text-xs w-6 shrink-0">
                          {String(i + 1).padStart(2, '0')}
                        </span>
                        <Icon className={`h-4 w-4 shrink-0 transition-colors ${active ? 'text-[#D4AF37]' : 'text-white/40 group-hover:text-[#2A9D8F]'}`} />
                        <div className="flex-1 min-w-0">
                          <span className={`text-base sm:text-lg font-display font-medium block transition-colors leading-tight ${
                            active ? 'text-[#D4AF37]' : 'text-white group-hover:text-[#2A9D8F]'
                          }`}>
                            {link.name}
                          </span>
                          <span className="text-white/35 text-[11px]">{link.desc}</span>
                        </div>
                        <ArrowRight className={`h-3.5 w-3.5 transition-all ${
                          active ? 'text-[#D4AF37] opacity-100' : 'text-white/20 opacity-0 group-hover:opacity-100 -translate-x-2 group-hover:translate-x-0'
                        }`} />
                      </Link>
                    );
                  })}
                </div>

                {/* RIGHT — Destinations grid (desktop only) */}
                <div className="hidden lg:block">
                  <div className="flex items-center gap-2 mb-3">
                    <MapPin className="h-3.5 w-3.5 text-[#D4AF37]/60" />
                    <span className="text-[#D4AF37]/60 font-mono text-[10px] uppercase tracking-[0.3em]">7 Destinations</span>
                    <div className="flex-1 h-px bg-gradient-to-r from-[#D4AF37]/30 to-transparent" />
                  </div>
                  <div className="grid grid-cols-2 xl:grid-cols-3 gap-2.5">
                    {destinationsList.map((d, i) => (
                      <Link
                        key={d.slug}
                        to={`/${d.slug}`}
                        onClick={closeMenu}
                        className={`group relative aspect-[4/3] overflow-hidden rounded-lg border border-white/10 hover:border-[#D4AF37]/60 transition-all duration-500 ${
                          animating ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'
                        }`}
                        style={{ transitionDelay: animating ? `${300 + i * 60}ms` : '0ms' }}
                        data-testid={`menu-destination-${d.slug}`}
                      >
                        <img
                          src={d.image}
                          alt={d.name}
                          className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                          loading="lazy"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-[#0A2540]/95 via-[#0A2540]/50 to-transparent" />
                        <div className="absolute inset-0 p-3 flex flex-col justify-end">
                          <span className="text-[#D4AF37]/90 text-[9px] uppercase tracking-[0.18em] font-semibold mb-0.5">
                            {d.region} · {d.duration}
                          </span>
                          <span className="text-white font-display text-sm font-bold leading-tight drop-shadow group-hover:text-[#2A9D8F] transition-colors">
                            {d.name}
                          </span>
                        </div>
                        <div className="absolute top-2 right-2 w-7 h-7 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                          <ArrowRight className="h-3.5 w-3.5 text-white" />
                        </div>
                      </Link>
                    ))}

                    {/* Day Charter — sorties à la journée (filling the 2 remaining slots) */}
                    {dayChartersList.map((d, i) => (
                      <Link
                        key={d.href}
                        to={d.href}
                        onClick={closeMenu}
                        className={`group relative aspect-[4/3] overflow-hidden rounded-lg border-2 border-dashed border-[#2A9D8F]/40 hover:border-[#2A9D8F] transition-all duration-500 ${
                          animating ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'
                        }`}
                        style={{ transitionDelay: animating ? `${300 + (destinationsList.length + i) * 60}ms` : '0ms' }}
                        data-testid={`menu-day-charter-${i}`}
                      >
                        <img
                          src={d.image}
                          alt={d.name}
                          className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                          loading="lazy"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-[#0A2540]/95 via-[#0A2540]/55 to-[#2A9D8F]/15" />

                        {/* Day Charter top-left ribbon */}
                        <div className="absolute top-2 left-2 flex items-center gap-1 px-2 py-0.5 bg-[#2A9D8F]/90 text-white rounded-full text-[9px] font-bold uppercase tracking-wider shadow-lg">
                          <span className="w-1 h-1 rounded-full bg-white animate-pulse" />
                          Day Charter
                        </div>

                        <div className="absolute inset-0 p-3 flex flex-col justify-end">
                          <span className="text-[#2A9D8F] text-[9px] uppercase tracking-[0.18em] font-semibold mb-0.5">
                            {d.region} · {d.duration}
                          </span>
                          <span className="text-white font-display text-sm font-bold leading-tight drop-shadow group-hover:text-[#3AB8A8] transition-colors">
                            {d.name}
                          </span>
                        </div>
                        <div className="absolute top-2 right-2 w-7 h-7 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                          <ArrowRight className="h-3.5 w-3.5 text-white" />
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Bottom: CTA + Phone */}
            <div
              className={`px-6 sm:px-10 pb-6 pt-3 transition-all duration-500 ${
                animating ? 'translate-y-0 opacity-100' : 'translate-y-6 opacity-0'
              }`}
              style={{ transitionDelay: animating ? '550ms' : '0ms' }}
            >
              <div className="max-w-7xl mx-auto space-y-2">
                <div className="h-px bg-gradient-to-r from-transparent via-[#D4AF37]/20 to-transparent mb-2" />
                <div className="flex flex-col sm:flex-row items-center gap-3">
                  <Button asChild className="flex-1 w-full sm:w-auto bg-[#C4724F] hover:bg-[#A0552F] text-white font-semibold h-12 text-sm rounded-xl" data-testid="menu-cta-reserve">
                    <Link to="/reservation-cabines-privatisation-catamaran-sognudimare" onClick={closeMenu}>
                      <Anchor className="mr-2 h-4 w-4" /> Réserver ma croisière
                    </Link>
                  </Button>
                  <a href="tel:0495729028" className="flex items-center justify-center gap-2 px-4 py-3 text-white/70 hover:text-white font-medium text-sm transition-colors border border-white/15 rounded-xl hover:border-white/30">
                    <Phone className="h-4 w-4" /> 04 95 72 90 28
                  </a>
                </div>
                <p className="text-center text-white/20 text-[10px] tracking-widest uppercase pt-1">Croisières catamaran en Corse, Sardaigne & Méditerranée</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Navbar;
